import { useState } from "react";
import { Plus, Pencil, LogIn, Lock, Unlock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api } from "../lib/api";
import { fdate, run, useLoad } from "../lib/util";
import { Badge, Btn, Field, Input, Select, Table, Empty } from "../components/kit";
import { toggleBlock } from "./PaymentsTab";
import { CredentialsDialog, genPassword } from "./Credentials";

const SUB = [["teste", "Em teste"], ["ativa", "Ativa"], ["em_atraso", "Em atraso"], ["suspensa", "Bloqueada"], ["cancelada", "Cancelada"]];
const TONE = { teste: "info", ativa: "green", em_atraso: "amber", suspensa: "red", cancelada: "muted" };
const EMPTY = { name: "", code: "", regional: "", municipio: "", uf: "RJ", gestor_name: "", email: "", whatsapp: "", ref_date: "", telefone: "", endereco: "", diretor_nome: "", diretor_matricula: "", plan_id: "", subscription_status: "teste" };

function ManagerFields({ m, setM }) {
  const set = (k) => (e) => setM({ ...m, [k]: e.target.value });
  return (
    <div className="rounded-xl border border-orange-200 bg-[#FFF8F3] p-4 space-y-3" data-testid="school-manager-section">
      <label className="flex items-center gap-2 text-sm font-semibold text-[#0B192C]">
        <input type="checkbox" data-testid="school-manager-checkbox" checked={m.on} onChange={(e) => setM({ ...m, on: e.target.checked })} />Criar agora o login e a senha do gestor
      </label>
      {m.on && (
        <div className="grid grid-cols-[minmax(0,1fr)] sm:grid-cols-2 gap-3 [&_input]:min-w-0">
          <Field label="Nome do gestor"><Input data-testid="school-manager-name" value={m.name} onChange={set("name")} /></Field>
          <Field label="Login (e-mail ou usuário)"><Input data-testid="school-manager-login" autoCapitalize="none" value={m.email} onChange={set("email")} placeholder="ex.: cepiranema ou gestor@escola.com" /></Field>
          <Field label="Senha (mín. 8)" className="sm:col-span-2">
            <div className="flex gap-2"><Input data-testid="school-manager-password" className="font-mono" value={m.password} onChange={set("password")} /><Btn variant="outline" type="button" data-testid="school-manager-generate" onClick={() => setM({ ...m, password: genPassword() })}>Gerar</Btn></div>
          </Field>
        </div>
      )}
    </div>
  );
}

function SchoolDialog({ initial, plans, onClose, onSaved, onCreated }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [m, setM] = useState({ on: !initial.id, name: "", email: "", password: genPassword() });
  const [logo, setLogo] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = Object.fromEntries(Object.entries(EMPTY).map(([k]) => [k, f[k] === "" ? null : f[k]]));
    body.subscription_status = f.subscription_status;
    const manager = !f.id && m.on ? { name: m.name || f.gestor_name || f.name, email: m.email, password: m.password } : null;
    const r = await run(() => (f.id ? api.put(`/admin/schools/${f.id}`, body) : api.post("/admin/schools", { ...body, manager })), "Escola salva");
    if (!r) return;
    if (logo) {
      const fd = new FormData();
      fd.append("file", logo);
      await run(() => api.post(`/admin/schools/${r.data.id}/logo`, fd), "Logomarca enviada");
    }
    onSaved();
    onClose();
    if (manager) onCreated({ school: r.data.name, name: manager.name, login: r.data.manager.email, password: manager.password, whatsapp: f.whatsapp });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl w-[calc(100%-1.5rem)] rounded-xl max-h-[90dvh] !flex flex-col gap-0 p-0 overflow-hidden" data-testid="school-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0"><DialogTitle>{f.id ? "Editar escola" : "Cadastrar escola"}</DialogTitle></DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3" data-testid="school-dialog-body">
        <div className="grid grid-cols-[minmax(0,1fr)] sm:grid-cols-2 gap-3 [&_input]:min-w-0 [&_select]:min-w-0">
          <Field label="Nome da escola" className="sm:col-span-2"><Input data-testid="school-name-input" value={f.name} onChange={set("name")} /></Field>
          <Field label="Código de identificação"><Input data-testid="school-code-input" value={f.code} onChange={set("code")} /></Field>
          <Field label="Regional Metropolitana"><Input data-testid="school-regional-input" value={f.regional || ""} onChange={set("regional")} /></Field>
          <Field label="Município"><Input data-testid="school-municipio-input" value={f.municipio} onChange={set("municipio")} /></Field>
          <Field label="Estado (UF)"><Input data-testid="school-uf-input" maxLength={2} value={f.uf} onChange={set("uf")} /></Field>
          <Field label="Nome do gestor"><Input data-testid="school-gestor-input" value={f.gestor_name || ""} onChange={set("gestor_name")} /></Field>
          <Field label="E-mail de contato da escola"><Input data-testid="school-email-input" type="email" value={f.email || ""} onChange={set("email")} /></Field>
          <Field label="WhatsApp"><Input data-testid="school-whatsapp-input" value={f.whatsapp || ""} onChange={set("whatsapp")} /></Field>
          <Field label="Data de referência dos documentos"><Input data-testid="school-refdate-input" type="date" value={f.ref_date || ""} onChange={set("ref_date")} /></Field>
          <Field label="Telefone"><Input data-testid="school-telefone-input" value={f.telefone || ""} onChange={set("telefone")} /></Field>
          <Field label="Endereço" className="sm:col-span-2"><Input data-testid="school-endereco-input" value={f.endereco || ""} onChange={set("endereco")} /></Field>
          <Field label="Diretor(a) da U.E."><Input data-testid="school-diretor-input" value={f.diretor_nome || ""} onChange={set("diretor_nome")} /></Field>
          <Field label="Matrícula / ID do diretor"><Input data-testid="school-matricula-input" value={f.diretor_matricula || ""} placeholder="ex.: Matr.: 0000000-0 ID.:0000000-0" onChange={set("diretor_matricula")} /></Field>
          <Field label="Plano"><Select data-testid="school-plan-select" value={f.plan_id || ""} onChange={set("plan_id")} placeholder="Sem plano" options={plans.map((p) => [p.id, p.name])} /></Field>
          <Field label="Situação da assinatura"><Select data-testid="school-status-select" value={f.subscription_status} onChange={set("subscription_status")} options={SUB} /></Field>
          <Field label="Logomarca (PNG/JPG)" className="sm:col-span-2"><input data-testid="school-logo-input" type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setLogo(e.target.files[0])} className="text-sm" /></Field>
        </div>
        {f.id ? <p className="text-xs text-slate-500">Para criar outro acesso ou redefinir a senha, use a aba “Gestores”.</p> : <ManagerFields m={m} setM={setM} />}
        </div>
        <DialogFooter className="px-6 py-3 border-t bg-white shrink-0 flex-row justify-end gap-2" data-testid="school-dialog-footer">
          <Btn variant="outline" data-testid="school-cancel-button" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="school-save-button" onClick={save}>Salvar</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SchoolsTab({ onEnter }) {
  const [schools, reload] = useLoad(async () => (await api.get("/admin/schools")).data, []);
  const [plans] = useLoad(async () => (await api.get("/admin/plans")).data, []);
  const [edit, setEdit] = useState(null);
  const [cred, setCred] = useState(null);
  const planName = (id) => (plans || []).find((p) => p.id === id)?.name || "—";
  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end"><Btn data-testid="new-school-button" onClick={() => setEdit({})}><Plus className="w-4 h-4" />Nova escola</Btn></div>
      <Table testid="schools-table">
        <thead><tr><th>Escola</th><th>Código</th><th>Município/UF</th><th>Regional</th><th>Gestor</th><th>Plano</th><th>Assinatura</th><th>Ref.</th><th /></tr></thead>
        <tbody>
          {(schools || []).map((s) => (
            <tr key={s.id} data-testid={`school-row-${s.code}`}>
              <td className="font-semibold text-[#0B192C]">{s.name}</td>
              <td className="font-mono text-xs">{s.code}</td>
              <td>{s.municipio}/{s.uf}</td>
              <td>{s.regional}</td>
              <td>{s.gestor_name}<div className="text-xs text-slate-500">{s.email}</div></td>
              <td>{planName(s.plan_id)}</td>
              <td><Badge tone={TONE[s.subscription_status]}>{SUB.find((x) => x[0] === s.subscription_status)?.[1]}</Badge></td>
              <td>{fdate(s.ref_date)}</td>
              <td className="whitespace-nowrap">
                <Btn variant="ghost" data-testid={`edit-school-${s.code}`} onClick={() => setEdit(s)}><Pencil className="w-4 h-4" /></Btn>
                {s.subscription_status === "suspensa"
                  ? <Btn variant="outline" data-testid={`unblock-school-${s.code}`} onClick={() => toggleBlock(s, reload)}><Unlock className="w-4 h-4" />Desbloquear</Btn>
                  : <Btn variant="ghost" title="Bloquear unidade" data-testid={`block-school-${s.code}`} onClick={() => toggleBlock(s, reload)}><Lock className="w-4 h-4 text-red-600" /></Btn>}
                <Btn variant="navy" data-testid={`enter-school-${s.code}`} onClick={() => onEnter(s)}><LogIn className="w-4 h-4" />Acessar dados</Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {schools && !schools.length && <Empty>Nenhuma escola cadastrada.</Empty>}
      {edit && <SchoolDialog initial={edit} plans={plans || []} onClose={() => setEdit(null)} onSaved={reload} onCreated={setCred} />}
      {cred && <CredentialsDialog cred={cred} onClose={() => setCred(null)} />}
    </div>
  );
}
